import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const emptyRegimenForm = {
  id: null,
  code: '',
  description: '',
};

const AdminRegimen = () => {
  const [dossiers, setDossiers] = useState([]);
  const [regimens, setRegimens] = useState([]);
  const [selectedDossierId, setSelectedDossierId] = useState('');
  const [selectedRegimenIds, setSelectedRegimenIds] = useState([]);
  const [useDossierOverrides, setUseDossierOverrides] = useState(true);
  const [regimenForm, setRegimenForm] = useState(emptyRegimenForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingRegimen, setSavingRegimen] = useState(false);
  const [resendingVenvers, setResendingVenvers] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const [deletingRegimenId, setDeletingRegimenId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const selectedDossier = useMemo(() => {
    return dossiers.find((dossier) => String(dossier.id) === String(selectedDossierId));
  }, [dossiers, selectedDossierId]);

  const canResendVenvers = Boolean(useDossierOverrides && selectedRegimenIds.length > 0 && selectedDossierId);

  const loadData = () => {
    setLoading(true);
    setError('');
    setMessage('');

    axios.get('/api/administrator/regimen')
      .then((response) => {
        const data = response.data || {};
        const loadedDossiers = data.dossiers || [];

        setDossiers(loadedDossiers);
        setRegimens(data.regimens || []);
        setUseDossierOverrides(Boolean(data.settings?.use_dossier_regimen_overrides));

        if (loadedDossiers.length > 0) {
          const configuredDossierId = data.configured_dossier_id ? String(data.configured_dossier_id) : null;
          const configured = configuredDossierId
            ? loadedDossiers.find((dossier) => String(dossier.id) === configuredDossierId)
            : loadedDossiers.find((dossier) => (dossier.fiscal_regimen_ids || []).length > 0);
          const current = configured || loadedDossiers.find((dossier) => String(dossier.id) === String(selectedDossierId)) || loadedDossiers[0];
          setSelectedDossierId(String(current.id));
          setSelectedRegimenIds((current.fiscal_regimen_ids || []).map(String));
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'No fue posible cargar la configuración de regímenes.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  const clearNotices = () => {
    setMessage('');
    setError('');
    setResendResult(null);
  };

  const handleDossierChange = (e) => {
    const dossierId = e.target.value;
    const dossier = dossiers.find((item) => String(item.id) === String(dossierId));

    setSelectedDossierId(dossierId);
    setSelectedRegimenIds(((dossier && dossier.fiscal_regimen_ids) || []).map(String));
    clearNotices();
  };

  const toggleRegimen = (regimenId) => {
    const id = String(regimenId);
    setResendResult(null);

    setSelectedRegimenIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }

      return [...prev, id];
    });
  };

  const handleSettingsToggle = () => {
    const nextValue = !useDossierOverrides;
    setSavingSettings(true);
    clearNotices();

    axios.post('/api/administrator/regimen/settings', {
      use_dossier_regimen_overrides: nextValue,
    })
      .then((response) => {
        setUseDossierOverrides(Boolean(response.data?.settings?.use_dossier_regimen_overrides));
        setMessage(nextValue
          ? 'Switch encendido: Venvers usará configuración por plantilla cuando exista.'
          : 'Switch apagado: Venvers mandará siempre people.regimen_id.');
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'No fue posible guardar el switch.');
      })
      .finally(() => {
        setSavingSettings(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedDossierId) {
      setError('Selecciona una plantilla de expediente.');
      return;
    }

    setSaving(true);
    clearNotices();

    axios.post(`/api/administrator/regimen/${selectedDossierId}`, {
      fiscal_regimen_ids: selectedRegimenIds.map((id) => parseInt(id, 10)),
    })
      .then((response) => {
        const updatedIds = (response.data?.fiscal_regimen_ids || []).map(String);

        setSelectedRegimenIds(updatedIds);
        setDossiers((prev) => prev.map((dossier) => {
          if (String(dossier.id) !== String(selectedDossierId)) {
            return dossier;
          }

          return {
            ...dossier,
            fiscal_regimen_ids: response.data?.fiscal_regimen_ids || [],
            fiscal_regimen_codes: response.data?.fiscal_regimen_codes || [],
          };
        }));
        setMessage('Configuración de plantilla guardada.');
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'No fue posible guardar la configuración.');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const resendToVenvers = () => {
    if (!canResendVenvers) {
      return;
    }

    if (!window.confirm(`¿Reenviar a Venvers todos los proveedores activos de la plantilla "${selectedDossier?.title || selectedDossierId}"?`)) {
      return;
    }

    setResendingVenvers(true);
    clearNotices();

    axios.post(`/api/administrator/regimen/${selectedDossierId}/resend-venvers`, {
      fiscal_regimen_ids: selectedRegimenIds.map((id) => parseInt(id, 10)),
    })
      .then((response) => {
        setResendResult(response.data || null);
        setMessage(`Reenvío terminado: ${response.data?.sent_count || 0} enviados, ${response.data?.failed_count || 0} fallidos.`);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'No fue posible reenviar a Venvers.');
      })
      .finally(() => {
        setResendingVenvers(false);
      });
  };

  const handleRegimenFormChange = (e) => {
    const { name, value } = e.target;
    setRegimenForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const editRegimen = (regimen) => {
    setRegimenForm({
      id: regimen.id,
      code: String(regimen.code),
      description: regimen.description || '',
    });
    clearNotices();
  };

  const resetRegimenForm = () => {
    setRegimenForm(emptyRegimenForm);
  };

  const saveRegimen = (e) => {
    e.preventDefault();
    setSavingRegimen(true);
    clearNotices();

    const payload = {
      code: parseInt(regimenForm.code, 10),
      description: regimenForm.description,
    };

    const request = regimenForm.id
      ? axios.put(`/api/administrator/regimen/catalog/${regimenForm.id}`, payload)
      : axios.post('/api/administrator/regimen/catalog', payload);

    request
      .then(() => {
        resetRegimenForm();
        setMessage('Régimen guardado.');
        loadData();
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'No fue posible guardar el régimen.');
      })
      .finally(() => {
        setSavingRegimen(false);
      });
  };

  const deleteRegimen = (regimen) => {
    if (!window.confirm(`¿Eliminar/desactivar el régimen ${regimen.code}? Se quitará de las plantillas configuradas.`)) {
      return;
    }

    setDeletingRegimenId(regimen.id);
    clearNotices();

    axios.delete(`/api/administrator/regimen/catalog/${regimen.id}`)
      .then(() => {
        setMessage('Régimen desactivado.');
        loadData();
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'No fue posible eliminar el régimen.');
      })
      .finally(() => {
        setDeletingRegimenId(null);
      });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-5">
        <div>
          <h3 className="mb-2">Regímenes fiscales por plantilla</h3>
          <p className="text-muted mb-0">
            Configura si Venvers usa arreglos de regímenes por plantilla o siempre el régimen fiscal del proveedor.
          </p>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {message ? <div className="alert alert-success">{message}</div> : null}

      {loading ? (
        <div className="text-muted">Cargando configuración...</div>
      ) : (
        <>
          <div className="card border mb-6">
            <div className="card-body d-flex justify-content-between align-items-center flex-wrap">
              <div className="mb-3 mb-md-0">
                <h5 className="mb-1">Usar configuración por plantilla</h5>
                <div className="text-muted">
                  Encendido: si la plantilla tiene regímenes configurados, Venvers recibe un arreglo. Apagado: siempre manda people.regimen_id.
                </div>
              </div>
              <button
                type="button"
                className={`btn ${useDossierOverrides ? 'btn-success' : 'btn-light'}`}
                disabled={savingSettings}
                onClick={handleSettingsToggle}
              >
                {savingSettings ? 'Guardando...' : (useDossierOverrides ? 'Encendido' : 'Apagado')}
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mb-8">
            <div className="form-group">
              <label className="font-weight-bold">Plantilla de expediente</label>
              <select
                className="form-control"
                value={selectedDossierId}
                onChange={handleDossierChange}
              >
                {dossiers.map((dossier) => (
                  <option key={dossier.id} value={dossier.id}>
                    {dossier.title} (ID {dossier.id})
                  </option>
                ))}
              </select>
              {selectedDossier ? (
                <small className="form-text text-muted">
                  Empresa people_id: {selectedDossier.people_id}
                </small>
              ) : null}
            </div>

            <div className="form-group">
              <label className="font-weight-bold">Regímenes a enviar a Venvers</label>
              <div className="row">
                {regimens.map((regimen) => {
                  const checked = selectedRegimenIds.includes(String(regimen.id));

                  return (
                    <div className="col-md-6 col-xl-4 mb-3" key={regimen.id}>
                      <label className="d-flex align-items-start border rounded p-3 h-100" style={{cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          className="mr-3 mt-1"
                          checked={checked}
                          onChange={() => toggleRegimen(regimen.id)}
                        />
                        <span>
                          <span className="font-weight-bold d-block">{regimen.code}</span>
                          <span className="text-muted">{regimen.description}</span>
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div className="mb-3 mb-md-0">
                {canResendVenvers ? (
                  <button
                    type="button"
                    className="btn btn-warning"
                    disabled={resendingVenvers || saving || !selectedDossierId}
                    onClick={resendToVenvers}
                  >
                    {resendingVenvers ? 'Reenviando...' : 'Reenviar a Venvers'}
                  </button>
                ) : null}
              </div>
              <button type="submit" className="btn btn-primary" disabled={saving || !selectedDossierId}>
                {saving ? 'Guardando...' : 'Guardar configuración de plantilla'}
              </button>
            </div>
          </form>

          {resendResult ? (
            <div className="card border mb-8">
              <div className="card-body">
                <h5>Resultado del reenvío</h5>
                <div className="mb-3">
                  Total: {resendResult.total} | Enviados: {resendResult.sent_count} | Fallidos: {resendResult.failed_count}
                </div>
                {resendResult.failed && resendResult.failed.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>RFC</th>
                          <th>Person provider ID</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resendResult.failed.slice(0, 20).map((item, index) => (
                          <tr key={`${item.person_provider_id}-${index}`}>
                            <td>{item.rfc || '-'}</td>
                            <td>{item.person_provider_id || '-'}</td>
                            <td>{item.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {resendResult.failed.length > 20 ? (
                      <div className="text-muted">Se muestran los primeros 20 fallidos.</div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="separator separator-dashed my-8"></div>

          <div className="row">
            <div className="col-lg-5 mb-6">
              <h5>{regimenForm.id ? 'Editar régimen' : 'Agregar régimen'}</h5>
              <form onSubmit={saveRegimen}>
                <div className="form-group">
                  <label>Código</label>
                  <input
                    type="number"
                    min="1"
                    name="code"
                    className="form-control"
                    value={regimenForm.code}
                    onChange={handleRegimenFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Descripción</label>
                  <input
                    type="text"
                    name="description"
                    className="form-control"
                    value={regimenForm.description}
                    onChange={handleRegimenFormChange}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary mr-2" disabled={savingRegimen}>
                  {savingRegimen ? 'Guardando...' : 'Guardar régimen'}
                </button>
                {regimenForm.id ? (
                  <button type="button" className="btn btn-light" onClick={resetRegimenForm}>
                    Cancelar
                  </button>
                ) : null}
              </form>
            </div>

            <div className="col-lg-7">
              <h5>Catálogo de regímenes</h5>
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Descripción</th>
                      <th className="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regimens.map((regimen) => (
                      <tr key={regimen.id}>
                        <td className="font-weight-bold">{regimen.code}</td>
                        <td>{regimen.description}</td>
                        <td className="text-right">
                          <button type="button" className="btn btn-light-primary btn-sm mr-2" onClick={() => editRegimen(regimen)}>
                            Editar
                          </button>
                          <button
                            type="button"
                            className="btn btn-light-danger btn-sm"
                            disabled={deletingRegimenId === regimen.id}
                            onClick={() => deleteRegimen(regimen)}
                          >
                            {deletingRegimenId === regimen.id ? 'Eliminando...' : 'Eliminar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminRegimen;
